import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, CalendarClock, Check, Undo2, UserMinus, X } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import type { Application } from '../api';
import {
  useAgendarEtapa,
  useAprovarCandidatura,
  useEtapas,
  useIndicadoresFunil,
  useMovimentarCandidatura,
  usePainelProcesso,
  useReprovarCandidatura,
  useEnviarParaBancoTalentos,
  useVaga,
} from '../hooks';
import { AgendarModal } from '../components/AgendarModal';
import { IndicadoresFunil } from '../components/IndicadoresFunil';
import { MovimentarModal, type MovimentacaoTipo } from '../components/MovimentarModal';
import { CandidaturaStatusBadge } from '../components/RecrutamentoBadges';
import { formatClienteLabel, formatDateTimeBR } from '../components/format';
import { ConverterCandidatura } from '../../pessoas/components/ConverterCandidatura';

/** Etapa terminal: só ali a aprovação final é possível. */
const ETAPA_FINAL = 'aprovacao';

/**
 * Painel do processo seletivo de uma vaga — kanban por etapa.
 *
 * As colunas vêm do servidor (etapas habilitadas da vaga, na ordem do
 * funil), inclusive as vazias: uma coluna que desaparece esconderia o
 * gargalo. A movimentação é por diálogo, não por arrastar: a transição
 * exige parecer em vários casos e é validada no servidor.
 */
export default function ProcessoSeletivoPage(): React.ReactElement {
  const { jobOpeningId } = useParams<{ jobOpeningId: string }>();
  const { can } = usePermissions();

  const { data: vaga } = useVaga(jobOpeningId);
  const { data: painel, isLoading, isError } = usePainelProcesso(jobOpeningId);
  const { data: etapas } = useEtapas();
  const { data: indicadores, isLoading: carregandoIndicadores } = useIndicadoresFunil({
    jobOpeningId,
  });

  const movimentar = useMovimentarCandidatura();
  const aprovar = useAprovarCandidatura();
  const reprovar = useReprovarCandidatura();
  const bancoTalentos = useEnviarParaBancoTalentos();
  const agendar = useAgendarEtapa();

  const [acao, setAcao] = useState<{ tipo: MovimentacaoTipo; application: Application } | null>(
    null,
  );
  const [agendando, setAgendando] = useState<Application | null>(null);

  const labels = useMemo(
    () => new Map((etapas ?? []).map((etapa) => [etapa.key, etapa.label])),
    [etapas],
  );

  const podeRetornar = can(FEATURES.recrutamentoRetornoEtapa, 'edit');
  const podeMovimentar = can(FEATURES.recrutamentoProcessoSeletivo, 'edit');

  /** Etapa anterior habilitada nesta vaga — destino de um retorno. */
  function etapaAnterior(stageKey: string): string | undefined {
    const keys = painel?.stages.map((stage) => stage.key) ?? [];
    const index = keys.indexOf(stageKey);
    return index > 0 ? keys[index - 1] : undefined;
  }

  function etapaSeguinte(stageKey: string): string | undefined {
    const keys = painel?.stages.map((stage) => stage.key) ?? [];
    const index = keys.indexOf(stageKey);
    return index >= 0 && index < keys.length - 1 ? keys[index + 1] : undefined;
  }

  const mutacaoAtiva =
    movimentar.isPending || aprovar.isPending || reprovar.isPending || bancoTalentos.isPending;
  const erroAcao =
    ((movimentar.error ?? aprovar.error ?? reprovar.error ?? bancoTalentos.error) as Error | null)
      ?.message ?? null;

  function executarAcao(input: { parecer?: string; score?: number }) {
    if (!acao) return;
    const id = acao.application.id;
    const done = { onSuccess: () => setAcao(null) };

    if (acao.tipo === 'aprovar') {
      aprovar.mutate({ id, parecer: input.parecer }, done);
      return;
    }
    if (acao.tipo === 'reprovar') {
      reprovar.mutate({ id, reason: input.parecer ?? '' }, done);
      return;
    }
    if (acao.tipo === 'banco_talentos') {
      bancoTalentos.mutate({ id, reason: input.parecer }, done);
      return;
    }
    if (acao.tipo === 'retornar') {
      const destino = etapaAnterior(acao.application.currentStageKey);
      movimentar.mutate({ id, targetStageKey: destino, parecer: input.parecer }, done);
      return;
    }
    movimentar.mutate({ id, parecer: input.parecer, score: input.score }, done);
  }

  return (
    <PageContainer
      title={vaga?.title ?? 'Processo seletivo'}
      subtitle={
        vaga
          ? [
              `ID ${vaga.requisition?.code ?? '—'}`,
              `Cliente: ${formatClienteLabel(vaga.requisition?.client)}`,
              `${vaga.approvedCount}/${vaga.requisition?.vacanciesCount ?? '?'} vagas preenchidas`,
            ].join(' · ')
          : undefined
      }
      breadcrumbs={[{ label: 'Vagas', to: '/recrutamento/vagas' }, { label: 'Processo seletivo' }]}
    >
      <IndicadoresFunil data={indicadores} loading={carregandoIndicadores} />

      {isError && (
        <Alert variant="error">Não foi possível carregar o painel do processo seletivo.</Alert>
      )}

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : !painel ? null : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {painel.stages.map((stage) => (
            <section key={stage.key} className="w-72 flex-shrink-0">
              <header className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">{stage.label}</h3>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  {stage.count}
                </span>
              </header>

              <div className="space-y-2">
                {stage.applications.length === 0 && (
                  <EmptyState size="compact" title="Nenhum candidato" />
                )}

                {stage.applications.map((application) => (
                  <article
                    key={application.id}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {application.candidate?.fullName ?? application.protocol}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">{application.protocol}</p>
                    {vaga && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        Vaga {vaga.requisition?.code ?? '—'} ·{' '}
                        {formatClienteLabel(vaga.requisition?.client)}
                      </p>
                    )}
                    <div className="mt-2">
                      <CandidaturaStatusBadge status={application.status} />
                    </div>
                    {application.lastMovedAt && (
                      <p className="mt-1 text-xs text-gray-400">
                        Movido em {formatDateTimeBR(application.lastMovedAt)}
                      </p>
                    )}

                    {application.status === 'aprovado' && (
                      <ConverterCandidatura
                        applicationId={application.id}
                        candidateName={application.candidate?.fullName ?? application.protocol}
                        defaults={{ companyId: vaga?.requisition?.companyId, employmentType: vaga?.requisition?.jobType }}
                      />
                    )}

                    {podeMovimentar && application.status !== 'aprovado' &&
                      application.status !== 'reprovado' &&
                      application.status !== 'banco_talentos' && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={<CalendarClock className="h-3.5 w-3.5" />}
                            onClick={() => setAgendando(application)}
                          >
                            Agendar
                          </Button>

                          {stage.key === ETAPA_FINAL ? (
                            <Button
                              size="sm"
                              icon={<Check className="h-3.5 w-3.5" />}
                              onClick={() => setAcao({ tipo: 'aprovar', application })}
                            >
                              Aprovar
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              icon={<ArrowRight className="h-3.5 w-3.5" />}
                              onClick={() => setAcao({ tipo: 'avancar', application })}
                            >
                              Avançar
                            </Button>
                          )}

                          {podeRetornar && etapaAnterior(stage.key) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Undo2 className="h-3.5 w-3.5" />}
                              onClick={() => setAcao({ tipo: 'retornar', application })}
                            >
                              Retornar
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="danger"
                            icon={<X className="h-3.5 w-3.5" />}
                            onClick={() => setAcao({ tipo: 'reprovar', application })}
                          >
                            Reprovar
                          </Button>

                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<UserMinus className="h-3.5 w-3.5" />}
                            onClick={() => setAcao({ tipo: 'banco_talentos', application })}
                          >
                            Banco de talentos
                          </Button>
                        </div>
                      )}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {acao && (
        <MovimentarModal
          open
          tipo={acao.tipo}
          candidato={acao.application.candidate?.fullName ?? acao.application.protocol}
          etapaAtual={labels.get(acao.application.currentStageKey) ?? acao.application.currentStageKey}
          etapaDestino={
            acao.tipo === 'avancar'
              ? labels.get(etapaSeguinte(acao.application.currentStageKey) ?? '')
              : acao.tipo === 'retornar'
                ? labels.get(etapaAnterior(acao.application.currentStageKey) ?? '')
                : undefined
          }
          loading={mutacaoAtiva}
          error={erroAcao}
          onCancel={() => setAcao(null)}
          onConfirm={executarAcao}
        />
      )}

      {agendando && (
        <AgendarModal
          open
          candidato={agendando.candidate?.fullName ?? agendando.protocol}
          etapaAtual={labels.get(agendando.currentStageKey) ?? agendando.currentStageKey}
          isReagendamento={agendando.status === 'entrevista_agendada'}
          loading={agendar.isPending}
          error={(agendar.error as Error | null)?.message ?? null}
          onCancel={() => setAgendando(null)}
          onConfirm={(payload) =>
            agendar.mutate(
              { id: agendando.id, ...payload },
              { onSuccess: () => setAgendando(null) },
            )
          }
        />
      )}
    </PageContainer>
  );
}
